import { BaseShape } from "../game-base/base-game-types";
import { CanvasElement, Polygon, Rect } from "mini-canvas-engine";
import Rand from "rand-seed";
import { MazeSolver } from "./maze-solver";

import { MazeDefinition, MazeEntryExit, MazeOptions } from "./maze-types";

import { t } from "i18next";

export class MazeGeneratorError extends Error {}

type Direction = "n" | "s" | "w" | "e";

export interface Neighbours {
    n?: number;
    s?: number;
    w?: number;
    e?: number;
}

export interface MazeOptionsFromBitmap extends MazeOptions {
    board: boolean[][];
    boardBounds: Rect;
}

export class MazeGenerator {
    options: MazeOptions = {
        width: 0,
        height: 0
    };
    randomGenerator: Rand = new Rand();
    matrix: boolean[][] = [];

    public generate(options: MazeOptions): MazeDefinition {
        this.options = options;
        const seed = this.options.seed ? this.options.seed : String(Math.random());
        this.randomGenerator = new Rand(seed);
        let nodes = this.options.board ? this.generateNodesFromBoard(this.options.board) : this.generateNodes();
        nodes = this.generateMaze(nodes);
        this.getMatrix(nodes);
        this.removeMazeWalls();
        const entry = this.getEntryNodes();
        this.matrix[entry.start.gate.row][entry.start.gate.col] = false;
        this.matrix[entry.end.gate.row][entry.end.gate.col] = false;
        return {
            width: this.options.width,
            height: this.options.height,
            seed: seed,
            matrix: this.matrix,
            entry: entry
        };
    }

    private generateNodes() {
        const count = this.options.width * this.options.height;
        const nodes: boolean[][] = [];

        for (let i = 0; i < count; i++) {
            // visited, nswe
            nodes.push([false, true, true, true, true]);
        }

        return nodes;
    }

    private generateNodesFromBoard(board: boolean[][]) {
        const nodes: boolean[][] = [];

        for (let row = 0; row < this.options.height; row++) {
            for (let col = 0; col < this.options.width; col++) {
                nodes.push([board[row][col] === undefined ? true : !board[row][col], true, true, true, true]);
            }
        }

        return nodes;
    }
    private generateMaze(nodes: boolean[][]): boolean[][] {
        const mazeSize = nodes.length;
        const positionIndex = { n: 1, s: 2, w: 3, e: 4 };
        const oppositeIndex = { n: 2, s: 1, w: 4, e: 3 };

        if (!mazeSize) {
            return nodes;
        }

        let max = 0;
        const moveNodes: number[] = [];
        let visited = 0;
        let position = Math.floor(this.randomGenerator.next() * nodes.length);
        if (this.options.board) {
            while (!this.options.board[Math.floor(position / this.options.width)][position % this.options.width]) {
                position = Math.floor(this.randomGenerator.next() * nodes.length);
            }
        }

        let biasCount = 0;
        let biasFactor = 3;
        if (this.options.bias) {
            if ("horizontal" === this.options.bias) {
                biasFactor = 1 <= this.options.width / 100 ? Math.floor(this.options.width / 100) + 2 : 3;
            } else if ("vertical" === this.options.bias) {
                biasFactor = 1 <= this.options.height / 100 ? Math.floor(this.options.height / 100) + 2 : 3;
            }
        }

        // Set start node visited.
        nodes[position][0] = true;

        while (visited < mazeSize - 1) {
            if (this.options.debug) {
                console.log(`Visiting node ${position} (${position % this.options.width} x ${Math.floor(position / this.options.width)})`);
            }
            biasCount++;

            max++;
            if (this.options.maxMazeIterations && this.options.maxMazeIterations < max) {
                throw new MazeGeneratorError(t("msg:mazeGenerator.maxMazeSizeReached"));
            }

            const next = this.getNeighbours(position);
            let directions = Object.keys(next).filter((key: string) => {
                return -1 !== (<any>next)[key] && !nodes[(<any>next)[key]][0];
            }) as Direction[];
            if (this.options.debug) {
                console.log(`  Possible directions ${directions.join(",")}`);
            }

            if (this.options.bias && biasCount !== biasFactor) {
                directions = this.biasDirections(directions);
            } else {
                biasCount = 0;
            }

            if (directions.length) {
                ++visited;

                if (1 < directions.length) {
                    moveNodes.push(position);
                }

                const direction: Direction = directions[Math.floor(this.randomGenerator.next() * directions.length)];

                // Update current position
                nodes[position][positionIndex[direction]] = false;

                if (next[direction] === undefined) {
                    throw new MazeGeneratorError(t("msg:mazeGenerator.undefinedNextPosition"));
                } else {
                    // Set new position
                    position = next[direction] as number;
                }

                // Update next position
                nodes[position][oppositeIndex[direction]] = false;
                nodes[position][0] = true;
            } else {
                if (!moveNodes.length) {
                    break;
                }

                position = moveNodes.pop() ?? -1;
            }
        }

        return nodes;
    }

    private getNeighbours(position: number): Neighbours {
        const row = Math.floor(position / this.options.width);
        const col = position % this.options.width;
        return {
            n: row > 0 ? position - this.options.width : -1,
            s: row < this.options.height - 1 ? position + this.options.width : -1,
            w: col > 0 ? position - 1 : -1,
            e: col < this.options.width - 1 ? position + 1 : -1
        };
    }

    private biasDirections(directions: Direction[]) {
        const horizontal = -1 !== directions.indexOf("w") || -1 !== directions.indexOf("e");
        const vertical = -1 !== directions.indexOf("n") || -1 !== directions.indexOf("s");

        if ("horizontal" === this.options.bias && horizontal) {
            directions = directions.filter(function (key) {
                return "w" === key || "e" === key;
            });
        } else if ("vertical" === this.options.bias && vertical) {
            directions = directions.filter(function (key) {
                return "n" === key || "s" === key;
            });
        }

        return directions;
    }

    private getMatrix(nodes: boolean[][]) {
        this.matrix = [];
        const mazeSize = this.options.width * this.options.height;

        // Add the complete maze in a matrix
        // where 1 is a wall and 0 is a corridor.

        let row1: boolean[] = [];
        let row2: boolean[] = [];

        if (nodes.length !== mazeSize) {
            return;
        }

        for (let i = 0; i < mazeSize; i++) {
            if (!row1.length) {
                row1.push(true);
            }
            if (!row2.length) {
                row2.push(true);
            }

            if (this.options.board && !this.options.board[Math.floor(i / this.options.width)][i % this.options.width]) {
                // This square is disabled by the board
                row1.push(...[true, true]);
                row2.push(...[true, true]);
            } else if (nodes[i][1]) {
                row1.push(...[true, true]); // += "11";
                if (nodes[i][4]) {
                    row2.push(...[false, true]); // "01";
                } else {
                    row2.push(...[false, false]); // "00";
                }
            } else {
                const hasAbove = i - this.options.width > 0;
                const above = hasAbove && nodes[i - this.options.width][4];
                const hasNext = i < nodes.length - 1;
                const next = hasNext && nodes[i + 1][1];

                if (nodes[i][4]) {
                    row1.push(...[false, true]); // "01";
                    row2.push(...[false, true]); // "01";
                } else if (next || above) {
                    row1.push(...[false, true]); // "01";
                    row2.push(...[false, false]); // "00";
                } else {
                    row1.push(...[false, false]); // "00";
                    row2.push(...[false, false]); // "00";
                }
            }

            if (0 === (i + 1) % this.options.width) {
                this.matrix.push(row1);
                this.matrix.push(row2);
                row1 = [];
                row2 = [];
            }
        }

        // Add closing row
        this.matrix.push(Array(this.options.width * 2 + 1).fill(true));
    }

    private removeMazeWalls() {
        if (!this.options.numWallsToRemove || !this.matrix.length) {
            return;
        }

        const min = 1;
        const max = this.matrix.length - 1;
        const maxTries = this.options.maxWallsRemoveIterations ?? 300;
        let tries = 0;
        let wallsRemoved = 0;

        while (tries < maxTries) {
            tries++;

            // Did we reached the goal
            if (wallsRemoved >= this.options.numWallsToRemove) {
                break;
            }

            // Get random row from matrix
            let y = Math.floor(this.randomGenerator.next() * (max - min + 1)) + min;
            y = y === max ? y - 1 : y;

            const walls: number[] = [];
            const row = this.matrix[y];

            // Get walls from random row
            for (let i = 0; i < row.length; i++) {
                if (i === 0 || i === row.length - 1) {
                    continue;
                }

                const wall = row[i];
                if (wall) {
                    walls.push(i);
                }
            }

            // Shuffle walls randomly
            this.shuffleArray(walls);

            // Try breaking a wall for this row.
            for (let i = 0; i < walls.length; i++) {
                if (this.removeWall(y, walls[i])) {
                    // Wall can be removed
                    wallsRemoved++;
                    break;
                }
            }
        }
    }

    private removeWall(row: number, index: number) {
        // Remove wall if possible.
        const evenRow = row % 2 === 0;
        const evenIndex = index % 2 === 0;
        const wall = this.matrix[row][index];

        if (!wall) {
            return false;
        }

        if (!evenRow && evenIndex) {
            // Uneven row and even column
            const hasTop = row - 2 > 0 && this.matrix[row - 2][index];
            const hasBottom = row + 2 < this.matrix.length && this.matrix[row + 2][index];

            if (hasTop && hasBottom) {
                this.matrix[row][index] = false;
                return true;
            } else if (!hasTop && hasBottom) {
                const left = this.matrix[row - 1][index - 1];
                const right = this.matrix[row - 1][index + 1];
                if (left || right) {
                    this.matrix[row][index] = false;
                    return true;
                }
            } else if (!hasBottom && hasTop) {
                const left = this.matrix[row + 1][index - 1];
                const right = this.matrix[row + 1][index + 1];
                if (left || right) {
                    this.matrix[row][index] = false;
                    return true;
                }
            }
        } else if (evenRow && !evenIndex) {
            // Even row and uneven column
            const hasLeft = this.matrix[row][index - 2];
            const hasRight = this.matrix[row][index + 2];

            if (hasLeft && hasRight) {
                this.matrix[row][index] = false;
                return true;
            } else if (!hasLeft && hasRight) {
                const top = this.matrix[row - 1][index - 1];
                const bottom = this.matrix[row + 1][index - 1];
                if (top || bottom) {
                    this.matrix[row][index] = false;
                    return true;
                }
            } else if (!hasRight && hasLeft) {
                const top = this.matrix[row - 1][index + 1];
                const bottom = this.matrix[row + 1][index + 1];
                if (top || bottom) {
                    this.matrix[row][index] = false;
                    return true;
                }
            }
        }

        return false;
    }

    getEntryNodes(): MazeEntryExit {
        const row = this.options.height * 2 + 1 - 2;
        const col = this.options.width * 2 + 1 - 2;

        const entryNodes: MazeEntryExit = {
            start: { entry: { col: 1, row: 1 }, gate: { col: 0, row: 1 } },
            end: { entry: { col: col, row: row }, gate: { col: col + 1, row: row } }
        };

        const access = this.options.entryType;
        if ("horizontal" === this.options.entryType || "vertical" === this.options.entryType) {
            // TO DO: Horizontal and vertical entry with board
            let xy = "horizontal" === access ? row : col;
            xy = (xy - 1) / 2;
            const even = xy % 2 === 0;
            xy = even ? xy + 1 : xy;

            const start_col = "horizontal" === access ? 1 : xy;
            const start_row = "horizontal" === access ? xy : 1;
            const end_col = "horizontal" === access ? col : even ? start_col : start_col + 2;
            const end_row = "horizontal" === access ? (even ? start_row : start_row + 2) : row;
            const startgate = "horizontal" === access ? { col: 0, row: start_row } : { col: start_col, row: 0 };
            const endgate = "horizontal" === access ? { col: col + 1, row: end_row } : { col: end_col, row: row + 1 };

            entryNodes.start = { entry: { col: start_col, row: start_row }, gate: startgate };
            entryNodes.end = { entry: { col: end_col, row: end_row }, gate: endgate };
        } else if (this.options.board) {
            while (!this.options.board[0][Math.floor(entryNodes.start.entry.col / 2)]) {
                entryNodes.start.entry.col += 2;
            }
            entryNodes.start.gate.col = entryNodes.start.entry.col - 1;
            while (!this.options.board[this.options.height - 1][Math.floor(entryNodes.end.entry.col / 2)]) {
                entryNodes.end.entry.col -= 2;
            }
            entryNodes.end.gate.col = entryNodes.end.entry.col + 1;
        }

        return entryNodes;
    }

    /**
     * Helper method to output a maze as a string
     * @param maze Maze to convert to string
     */
    mazeToString(maze: boolean[][]) {
        let result = "";
        for (const row of maze) {
            for (const cell of row) {
                result += cell ? "■" : " ";
            }
            result += "\n";
        }
        return result;
    }

    public getMazeOptionsFromShape(shape: BaseShape, dimension: number): MazeOptionsFromBitmap {
        const minMax = Polygon.getPolygonBoundingRect(shape.dots);

        const shapeWidth = minMax.width;
        const shapeHeight = minMax.height;
        const mazeSquare = Math.max(shapeWidth, shapeHeight) / dimension;

        const mazeWidth = Math.floor(shapeWidth / mazeSquare);
        const mazeHeight = Math.floor(shapeHeight / mazeSquare);

        if (mazeWidth < 2 || mazeHeight < 2) {
            throw new MazeGeneratorError(t("msg:mazeGenerator.shapeTooSmall"));
        }

        const canvas = document.createElement("canvas");
        canvas.width = mazeWidth;
        canvas.height = mazeHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new MazeGeneratorError(t("msg:error.unableToGetCanvasContext"));
        }
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, mazeWidth, mazeHeight);
        ctx.beginPath();
        let first = true;
        for (const point of shape.dots) {
            if (first) {
                ctx.moveTo((point.x - minMax.left) / mazeSquare, (point.y - minMax.top) / mazeSquare);
            } else {
                ctx.lineTo((point.x - minMax.left) / mazeSquare, (point.y - minMax.top) / mazeSquare);
            }
            first = false;
        }
        ctx.closePath();
        ctx.fillStyle = "#ffffff";
        ctx.fill();

        const imageData = ctx.getImageData(0, 0, mazeWidth, mazeHeight);
        const mask: boolean[][] = [];

        let pixel = 0;
        for (let row = 0; row < imageData.height; row++) {
            const rowValues: boolean[] = [];
            mask.push(rowValues);
            for (let col = 0; col < imageData.width; col++) {
                rowValues.push(imageData.data[pixel] > 1);
                pixel += 4;
            }
        }

        // Make sure that the first and last points are not loose
        let startX = mask[0].indexOf(true);
        let endX = mask[mazeHeight - 1].lastIndexOf(true);
        let path = this.checkMaskHasSolution(
            mazeWidth,
            mazeHeight,
            mask.map((row) => row.map((cell) => !cell)),
            startX,
            endX
        );
        if (!path.length) {
            // If we don't find a solution, we set the values to true until we find a solution
            let distance = 1;

            while ((!path.length || distance == mazeHeight - 1 || distance == mazeWidth - 1) && distance < Math.max(mazeWidth, mazeHeight)) {
                if (CanvasElement._DEBUG_) {
                    console.debug("No path found, expanding mask distance to ", distance);
                    console.log(mask.map(row => row.map(col => col ? '⬛' : '⬜')).join('\n'));
                }
                for (let y = 0; y <= Math.min(distance, mazeHeight - 1); y++) {
                    for (let xDist = -distance; xDist <= distance; xDist++) {
                        const x = startX + xDist;
                        if (x >= 0 && x < mazeWidth && (y > 0 || xDist > 0) && !mask[y][x]) {
                            mask[y][x] = true;
                        }
                    }
                }

                for (let y = mazeHeight - 1; y >= Math.max(0, mazeHeight - 1 - distance); y--) {
                    for (let xDist = -distance; xDist <= distance; xDist++) {
                        const x = endX + xDist;
                        if (x >= 0 && x < mazeWidth && (y < mazeHeight - 1 || xDist < 0) && !mask[y][x]) {
                            mask[y][x] = true;
                        }
                    }
                }
                startX = mask[0].indexOf(true);
                endX = mask[mazeHeight - 1].lastIndexOf(true);
                path = this.checkMaskHasSolution(
                    mazeWidth,
                    mazeHeight,
                    mask.map((row) => row.map((cell) => !cell)),
                    startX,
                    endX
                );
                distance++;
            }
        }

        if (!path.length) {
            throw new MazeGeneratorError(t("msg:mazeGenerator.mazeHasNoSolution"));
        }

        // const img = document.createElement("img");
        // img.width = mazeWidth;
        // img.height = mazeHeight;
        // img.src = canvas.toDataURL();

        // document.body.insertBefore(img, document.body.children[0]);

        return {
            width: mazeWidth,
            height: mazeHeight,
            board: mask,
            boardBounds: minMax,
            shape: shape
        };
    }

    private checkMaskHasSolution(mazeWidth: number, mazeHeight: number, mask: boolean[][], startX: number, endX: number) {
        const solver = new MazeSolver();
        const mazeConfiguration = {
            width: mazeWidth,
            height: mazeHeight,
            matrix: mask,
            entry: {
              start: {
                entry: {
                  col: startX,
                  row: 0
                },
                gate: {
                  col: startX,
                  row: 0
                }
              },
              end: {
                entry: {
                  col: endX,
                  row: mazeHeight - 1
                },
                gate: {
                  col: endX,
                  row: mazeHeight - 1
                }
              }
            },
            seed: ""
        };
        try {
          const path = solver.solve(mazeConfiguration);
          return path;
        } catch (e) {
          console.error("Error checking maze solution", e, " for maze:", JSON.stringify(mazeConfiguration));
          return [];
        }
    }

    shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(this.randomGenerator.next() * (i + 1));
            const temp = array[i];
            array[i] = array[j];
            array[j] = temp;
        }
    }

    static getPictureMazeImagePosition(extendedOptions: MazeOptionsFromBitmap, shape: BaseShape, gameWidth: number, gameHeight: number) {
        const bounds = extendedOptions.boardBounds;
        const xFactor = shape.width / bounds.width;
        const width = Math.round(gameWidth * xFactor);
        const x = -Math.round((bounds.left * width) / shape.width);
        const yFactor = shape.height / bounds.height;
        const height = Math.round(gameHeight * yFactor);
        const y = -Math.round((bounds.top * height) / shape.height);
        return new Rect(x, y, width, height);
    }
}
